import LocalRsnChnlService, { ProductType, type LocalRsnChannel } from "@directv-digital/dds.apis.local-rsn-chnls";
import { APP_BASE_PATH } from "../utils/app-base-path";

/** Reads the <html> attribute written by `channelsServiceLocalsAttr` (lib/helper/channels-service-helpers.ts). */
export function isChannelsServiceLocalsOnInPage(): boolean {
  return typeof document !== "undefined" &&
    document?.documentElement?.getAttribute?.("data-channelservice-locals") === "true";
}

export type LocalsPlatform = "ott" | "satellite";

export const SERVICE_LOCALS_TIMEOUT_MS = 1500;

// AbortSignal.timeout is past the compile target and unpolyfilled, so an old
// browser gets the same limit from a plain controller instead of none.
function timeoutSignal(ms: number): AbortSignal | undefined {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return AbortSignal.timeout(ms);
  }
  if (typeof AbortController === "undefined") {
    return undefined;
  }
  const controller = new AbortController();
  setTimeout(() => controller?.abort?.(), ms);
  return controller.signal;
}

/**
 * WAT-11611 — the browser's single entry point for local TV stations sourced
 * from channels-service instead of the beam local-channel xapi.
 *
 * WHAT IT RETURNS, and why the contract is shaped this way:
 *   - `LocalRsnChannel[]` — channels-service answered; render these.
 *   - `null`              — do nothing different; run the beam call you run today.
 *
 * `null` covers every "not this path" case at once: the feature is off, the feed
 * has no such market, the network failed, the body was malformed. A caller
 * therefore needs ONE branch, not four, and no caller can accidentally treat a
 * degraded response as "this market has no local stations" and blank a lineup
 * the beam path renders fine.
 *
 * WHY THE FLAG IS AN <html> ATTRIBUTE, NOT A PROP. The server stamps
 * `data-channelservice-locals="true"` on the root element in both routers
 * (`pages/_document.tsx`, `app/layout.tsx`), so every page — including the agent
 * package-compare-tool pages, whose ApplicationContext carries only globalZipCode
 * and globalRsnFee — reads the same value without threading a boolean through
 * page props. With the switch off the attribute is absent and no request is made,
 * so production behaves exactly as it does today.
 */
export async function fetchChannelsServiceLocals(
  dma: string | number | undefined | null,
  platform: LocalsPlatform = "ott"
): Promise<LocalRsnChannel[] | null> {
  if (!isChannelsServiceLocalsOnInPage() || dma === undefined || dma === null || dma === "") {
    return null;
  }

  // No trailing slash, matching the working `/api/channel-data` fetch in
  // ChlupInteractiveToolV2. (The RUM beacon uses one because Akamai 301s the
  // slashless form and a 301 turns its POST into a bodyless GET; a GET carries
  // no body, so that hazard does not apply here.)
  //
  // APP_BASE_PATH is "/uf" in a production build and "" locally, inlined at
  // build time. It replaces reading `window.__NEXT_DATA__.assetPrefix`, which is
  // Pages-Router-only — on the App Router that global is absent, the prefix came
  // out "" and the fetch 404'd (WAT-11617).
  const url = `${APP_BASE_PATH}/api/local-channels?dma=${encodeURIComponent(
    String(dma)
  )}&platform=${encodeURIComponent(platform)}`;

  try {
    // Callers hold their spinner until this settles, so a slow request must not hold
    // the lineup; on timeout the catch below returns null and beam's locals stand.
    // The route only reads the server cache (milliseconds when healthy), so 1.5s is
    // generous and caps the worst-case wait a customer sees.
    const response = await fetch(url, { signal: timeoutSignal(SERVICE_LOCALS_TIMEOUT_MS) });
    // 404 is the route's "market not in the feed" answer, already logged server-side.
    if (response?.status === 404) {
      return null;
    }
    if (!response?.ok) {
      console.warn("[channels-service-locals] unexpected status, using beam", response?.status);
      return null;
    }
    const body = await response.json();

    // Only an actual array counts. A body without one means the server did not
    // serve this market, and inventing [] here is the blank-lineup bug.
    if (Array.isArray(body?.localChannels)) {
      return body.localChannels;
    }
    if (body?.enabled !== false) {
      console.warn("[channels-service-locals] body has no localChannels, using beam");
    }
    return null;
  } catch (error: any) {
    console.warn("[channels-service-locals] request failed, using beam", error?.name ?? error?.message);
    return null;
  }
}

/**
 * Which local list to render: channels-service is the source of truth, so its
 * stations win whenever it returns any — beam's answer is not consulted, not even
 * an empty per-ZIP one (owner ruling on WAT-11611, 2026-09-24: "if channelservice
 * returns station for a dma show it and thats it"). Beam is used only when the
 * service failed or had nothing for the market (`null` or `[]`), a fallback kept
 * until the beam locals are retired.
 *
 * Returns the input array itself, never a copy.
 */
export function pickLocals<S, B>(
  service: S[] | null | undefined,
  beam: B[] | null | undefined
): S[] | B[] | null | undefined {
  return (service?.length ?? 0) > 0 ? service : beam;
}

/**
 * Run a local station through the vendored `buildChannelObject`, whatever its
 * logo format. The ZipcodeModule, the CHLUP zipcode utils and channel-compare-tool-v3
 * all do this, so it lives here once.
 *
 * A beam logo arrives as `.../local-channels/<file>`; the builder wants the bare
 * `<file>` and re-prefixes it. A channels-service logo is already resolved (a
 * `/dtvassets/...` path or an OV URL) or empty, has no `local-channels/`
 * segment, and is kept as-is. The builder is still handed `""` for it because
 * it calls `logo.startsWith` unguarded — `undefined` there throws.
 */
export function buildLocalChannelObject(
  chnl: Partial<LocalRsnChannel> | undefined,
  productType: ProductType
) {
  const file = chnl?.logo?.split?.("local-channels/")?.[1];
  // The builder is typed for a full LocalRsnChannel but reads every field
  // optionally except `logo`, which is always a string here; hence the cast.
  const built = LocalRsnChnlService?.buildChannelObject?.(
    {
      ...chnl,
      logo: file ?? ""
    } as LocalRsnChannel,
    "local",
    productType
  );
  return file ? built : {
    ...built,
    logo: chnl?.logo
  };
}
